import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { Logo } from '../brand/Logo'

interface AccountTopBarProps {
  /** Where the logo leads; unlinked without one. */
  home?: { href: string; label: string }
  /** Before the logo, e.g. the button that opens the side nav on phones. */
  menuButton?: ReactNode
  /** The customer, straight after the logo. */
  org?: ReactNode
  /** The products, centred on the bar; a phone reaches them from the menu instead. */
  tabs?: ReactNode
  /** Page actions, before the profile. */
  actions?: ReactNode
  /** The signed-in person's menu, at the far right. */
  profile?: ReactNode
}

/** Every Cai app's signed-in top bar: the customer on the left, the products centred, the person on the right. */
export function AccountTopBar({ home, menuButton, org, tabs, actions, profile }: AccountTopBarProps) {
  const logo = <Logo symbolOnly theme="black" width={32} className="size-6 md:size-8" />

  return (
    // z-20: below the apps' drawers and dialogs (z-50), above their side nav (z-10).
    <header className="border-ink-100 sticky top-0 z-20 h-14 shrink-0 border-b bg-white px-2.5 md:h-16 md:px-5.5">
      {/* Three tracks so the products keep their own room and the sides truncate rather than run under them. */}
      <div className="flex h-full items-center gap-2 md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:gap-4.5">
        {/* On a phone the customer's name gives way first. */}
        <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-4.5">
          {menuButton}
          <div className="flex min-w-0 items-center gap-2 md:gap-4">
            {home ? (
              <Link to={home.href} aria-label={home.label} className="inline-flex shrink-0 leading-0">
                {logo}
              </Link>
            ) : (
              <span className="inline-flex shrink-0 leading-0">{logo}</span>
            )}
            {org}
          </div>
        </div>
        {tabs ? <div className="hidden h-full items-center md:flex">{tabs}</div> : <div />}
        {/* The profile keeps its room; wide actions (staff search) narrow within a capped share of a phone's bar. */}
        <div className="ml-auto flex shrink-0 items-center justify-end gap-2 max-md:max-w-[70%] md:gap-4.5">
          {actions && <div className="flex min-w-0 items-center justify-end gap-2 md:gap-4.5">{actions}</div>}
          {profile && <div className="shrink-0">{profile}</div>}
        </div>
      </div>
    </header>
  )
}
